import { useEffect, useRef } from 'react'
import { useSessionStore } from '@/store/sessionStore'
import type { SessionState } from '@/voice/types'
import { BlossomRenderer } from './renderer'
import styles from './Blossom.module.css'

/**
 * Whether this browser can give us a 2D context at all, decided once.
 *
 * A browser capability, not a per-element condition, so it is answered during
 * render rather than discovered in an effect and pushed back through state.
 * jsdom returns null here, which is what makes the CSS fallback ring the thing
 * component tests actually see.
 */
const CANVAS_2D_SUPPORTED = (() => {
  if (typeof document === 'undefined') return false
  try {
    return document.createElement('canvas').getContext('2d') !== null
  } catch {
    return false
  }
})()

export interface BlossomProps {
  state: SessionState
  muted?: boolean
  held?: boolean
  reducedMotion?: boolean
  /** Optional size override, any CSS length. */
  size?: string
  /**
   * Forces the level instead of reading it from the session store. Only used
   * by tests and by the welcome screen, where there is no session yet.
   */
  level?: number
}

/**
 * The audio-reactive Namu mark.
 *
 * Deliberately `aria-hidden`. An animated shape has nothing useful to say to a
 * screen reader, and describing its motion would be noise. The session state it
 * depicts is announced by the status region next to it, in words, which is both
 * more accurate and available to everyone.
 *
 * The audio level is read straight from the store by subscription rather than
 * arriving as a prop. Levels change sixty times a second, and as a prop each
 * one re-rendered this component, the captions, the ledger and the status line
 * with it. Now nothing above the canvas re-renders while someone is talking:
 * React handles the states, the render loop handles the pixels.
 */
export function Blossom({
  state,
  muted = false,
  held = false,
  reducedMotion = false,
  size,
  level,
}: BlossomProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const frameRef = useRef<number | null>(null)

  // Low-frequency props, read by the animation loop without re-subscribing it.
  const latest = useRef({ state, muted, held, reducedMotion, level })
  useEffect(() => {
    latest.current = { state, muted, held, reducedMotion, level }
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const renderer = new BlossomRenderer()
    renderer.attach(canvas)
    if (!renderer.isReady) {
      // No 2D context. The CSS fallback ring is already on screen, so there is
      // nothing to do but stay out of the way.
      return () => renderer.detach()
    }

    // Whoever holds the floor drives the orb. Blending the two meters would let
    // a noisy room make an idle assistant look like it is talking, which is the
    // only cue in the interface for whose turn it is.
    const readLevel = () => {
      const session = useSessionStore.getState()
      return session.state === 'model_speaking' ? session.outputLevel : session.inputLevel
    }

    let storeLevel = readLevel()
    const unsubscribe = useSessionStore.subscribe(() => {
      storeLevel = readLevel()
    })

    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(() => renderer.resize()) : null
    observer?.observe(canvas)

    // A canvas cannot notice a colour-scheme change the way a CSS rule does,
    // so the palette is re-read on both signals that can cause one: the OS
    // preference flipping, and the appearance setting writing `data-scheme`
    // onto the root element.
    const refresh = () => renderer.refreshPalette()
    const scheme = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null
    scheme?.addEventListener('change', refresh)
    const attributes = new MutationObserver(refresh)
    attributes.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-scheme'],
    })

    let lastPaint = 0
    const tick = (now: number) => {
      const current = latest.current
      // Reduced motion still repaints, because colour and weight carry state,
      // but at a fraction of the rate. Freezing entirely would hide the very
      // information the animation exists to convey.
      const minInterval = current.reducedMotion ? 125 : 0
      if (now - lastPaint >= minInterval) {
        lastPaint = now
        renderer.update({
          state: current.state,
          level: current.level ?? storeLevel,
          muted: current.muted,
          held: current.held,
          reducedMotion: current.reducedMotion,
        })
        renderer.draw(now)
      }
      frameRef.current = requestAnimationFrame(tick)
    }
    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
      unsubscribe()
      observer?.disconnect()
      scheme?.removeEventListener('change', refresh)
      attributes.disconnect()
      renderer.detach()
    }
  }, [])

  return (
    <div
      className={styles.wrap}
      style={size ? { width: size } : undefined}
      data-testid="orb"
      data-state={state}
      data-muted={muted || undefined}
      data-held={held || undefined}
      aria-hidden="true"
    >
      {CANVAS_2D_SUPPORTED ? (
        <canvas ref={canvasRef} className={styles.canvas} />
      ) : (
        /* Not decorative: without it, a browser that refuses canvas shows an
           empty square where the only state indicator should be. */
        <div className={styles.fallback}>
          <span className={styles.fallbackDot} />
        </div>
      )}
    </div>
  )
}
